import { action, makeObservable, observable } from 'mobx';

import { ApiClient } from '../../apiClient/ApiClient';
import { ErrorReporter } from '../../errorHandling/ErrorReporter';
import { TranslationKey } from '../../i18n/i18n';
import { Category } from '../../web-api/domain/category';

export interface CategoriesStoreState {
  categories: Category[] | null;
  intentCategories: Category[] | null;
}

export interface CategoryGroup {
  key: string;
  shortDisplayNameKey?: TranslationKey;
  displayNameKey: TranslationKey;
  categories: (Category | undefined)[];
}

export class CategoriesStore {
  private apiClient: ApiClient;
  private errorReporter: ErrorReporter;
  public categories: Category[] | null = null;
  private allCategoriesBySlug = new Map<string, Category>();
  public intentCategories: Category[] | null = null;
  public networkError: Error | null = null;

  constructor(
    {
      apiClient,
      errorReporter,
    }: {
      apiClient: ApiClient;
      errorReporter: ErrorReporter;
    },
    initialState?: CategoriesStoreState,
  ) {
    makeObservable<CategoriesStore, 'setCategories'>(this, {
      networkError: observable,
      getCategoryBySlug: action,
      initialFetch: action,
      setCategories: action,
    });

    this.apiClient = apiClient;
    this.errorReporter = errorReporter;
    if (initialState) {
      this.setCategories(initialState.categories);
      this.intentCategories = initialState.intentCategories;
    }
  }

  public getParentCategoryBySlug(slug: string): Category | undefined {
    return (this.categories ?? []).find(
      ({ subCategories }) => subCategories && subCategories.some(({ name }) => name === slug),
    );
  }

  public isCategoriesRelated(parentSlug: string, subCategorySlug: string): boolean {
    return this.getParentCategoryBySlug(subCategorySlug)?.name === parentSlug;
  }

  public get categoryGroups(): CategoryGroup[] {
    const categoryGroups: CategoryGroup[] = [
      {
        key: 'type',
        displayNameKey: 'categoriesMenu.categoryGroup.type',
        shortDisplayNameKey: 'categoriesMenu.categoryGroup.type',
        categories: ['one-page', 'online-store', 'portfolio-cv', 'blog'].map(this.getCategoryBySlug),
      },
      {
        key: 'Industry',
        displayNameKey: 'categoriesMenu.categoryGroup.industry',
        shortDisplayNameKey: 'categoriesMenu.categoryGroup.industry',
        categories: [
          'business',
          'industrial',
          'health-wellness',
          'events',
          'education',
          'communities',
          'fashion-and-style',
          'beauty-and-hair',
          'design',
          'photography',
          'music',
          'creative-arts',
          'video',
          'restaurants-food',
          'travel-tourism',
        ].map(this.getCategoryBySlug),
      },
    ];

    return categoryGroups
      .filter(Boolean)
      .map(({ categories, ...rest }) => ({
        ...rest,
        categories: categories.filter((category) => this.categories?.includes(category)),
      }))
      .filter(({ categories }) => categories.length > 0);
  }

  public getIntentCategoryBySlug = (slug: string): Category | undefined =>
    this.intentCategories.flatMap((ic) => ic.subCategories).find((ic) => ic.name === slug);

  public getCategoryBySlug = (slug: string): Category | undefined => this.allCategoriesBySlug.get(slug);

  public async initialFetch(): Promise<void> {
    try {
      const { items, intentCategories } = await this.apiClient.fetchCategories();
      this.setCategories(items);
      this.intentCategories = intentCategories;
    } catch (error) {
      this.networkError = error;
      this.errorReporter.reportError(error);
      this.setCategories(null);
      this.intentCategories = null;
    }
  }

  private setCategories(categories: Category[] | null) {
    const allCategoriesBySlug = new Map<string, Category>();
    for (const c of categories || []) {
      if (c.type === 'none') {
        continue;
      }
      allCategoriesBySlug.set(c.name, c);
      for (const s of c.subCategories || []) {
        allCategoriesBySlug.set(s.name, s);
      }
    }
    this.categories = categories;
    this.allCategoriesBySlug = allCategoriesBySlug;
  }

  serialize(): CategoriesStoreState {
    return {
      categories: this.categories,
      intentCategories: this.intentCategories,
    };
  }
}
